<script setup lang="ts">

import { TESelect, TESwitch, TESwitchLabel } from '@ben/vite-te'
import {InformationCircleIcon} from '@heroicons/vue/24/outline'
import { Head } from '@inertiajs/vue3'

import type { City, DynamicFields, StrikeRequest } from '@/Types/Models/City.ts'
import type { GeneralSettings, StrikeSettings } from '@/Types/Models/Settings.ts'

const props = defineProps<{
  cities: City[]
  strikeFields: DynamicFields[],
  nextGlobalStrikeDate?: string
  strikeSettings: Pick<StrikeSettings, 'city_required'|'form_heading'|'form_sub_heading'|'form_general_title'|'form_general_subtitle'|'form_advanced_subtitle'|'form_advanced_title'|'privacy_title'|'privacy_subtitle'>
}>()

const form = useForm<Partial<StrikeRequest>>(
  {
    requester_name: '',
    requester_email: '',
    requester_phone: '',
    city_name: '',
    starts_at: '',
    date: props.nextGlobalStrikeDate ?? '',
    start_location: '',
    link: '',
    note: '',
    internal_note: '',
    location: undefined,
    fields: Object.fromEntries(props.strikeFields.map((f) => [f.name, ""])) satisfies Record<string, string>
  }
)

if (form.fields && ('newsletter' in form.fields))
{
  form.fields['newsletter'] = false
}

const showMore = ref(true)

const grp = props.strikeFields.find(f => f.name==='group')
const options = grp ? grp.options : []

const requiredData = [
  { key: 'city_name', label: 'Stadt', required: true },
  {key: 'fields.group', label: 'Euer Team ist  / ihr seid', required: true, options: Array.isArray(options) ? options : undefined, type: 'multiselect'},
  {key: 'fields.team', label: 'Euer Teamname (nur wenn ihr ein Team seid)', required: false,},
  { key: 'link', label: 'Link zu eurer  Webseite, zu eurer E-Mail Adresse oder ein anderer Link, über die Interessierte an aktuelle Informationen zu eurer vor-Ort-Aktion kommen ', type: 'link' },
  { key: 'date', label: 'Datum', type: 'date', required: true },
  { key: 'starts_at', label: 'Startzeit', type: 'time' },
  { key: 'fields.ends_at', label: 'Endzeit', type: 'time' },
  { key: 'fields.technology', label: 'Technik (bitte kurz fassen!)' },
]
const additionalData = [
  { key: 'requester_name', label: 'Dein Name bzw. Name der Ansprechperson (AP)', required: true },
  { key: 'requester_email', label: 'E-Mail Adresse unter der wir euch gut erreichen', required: true, type: 'email' },
  // { key: 'start_location', label: 'Startpunkt (z.b. Marktplatz)' },
  { key: 'internal_note', label: 'Hinweiß ans Orgateam' },
  { key: 'requester_phone', label: 'Handynummer unter der wir euch gut erreichen' },
  { key: 'fields.press_phone', label: 'Handynummer Presse-Ansprechpartner:in eures Teams (wenn vorhanden) ' },
  { key: 'fields.press_email', label: 'E-Mail Presse-Ansprechpartner:in eures Teams (wenn vorhanden) ' },
  { key: 'fields.newsletter', label: 'Ich stimme zu, dass ich von LocalZero und/oder Greenpeace weitere Informationen zum TdoH erhalte.', type: 'checkbox' },
  // ...props.strikeFields.map((field) => ({
  //   key: `fields.${field.name}`,
  //   label: field.label,
  //   required: field.required,
  //   type: field.rules === 'select'? 'select': 'text',
  //     options: Array.isArray(field.options) ? field.options : undefined
  // }))
]

function submit() {
  if (props.strikeSettings.city_required && !form.city) {
    form.errors.city = 'Bitte wähle eine Ortsgruppe aus.'
    return
  }

  form.transform((data) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return { ...data, city_id: props.strikeSettings.city_required ? data.city!.id : undefined }
  }).post(route('requests.strike-request.store'), {
    onSuccess: () => {
      form.reset()
    }
  })
}

// const showMap = ref(false)
// function showMapStuff() {
//   form.location =
//     {
//       type: 'Point',
//       coordinates: [10, 51]
//     }
//   showMap.value = true
// }
const acceptedPrivacyPolicy = ref(false)

const readyToSubmit = computed(() => {
  if (props.strikeSettings.city_required && !form.city) return false
  return acceptedPrivacyPolicy.value;

})

const settings = usePage().props.settings as GeneralSettings
</script>

<template>
  <CenterLayout>
    <template #header>
      <HeaderWithAction class="max-w-6xl">

        <Head :title="settings.strike_word_singular" />

        <h1 class="text-2xl font-bold dark:text-white">{{strikeSettings.form_heading}}</h1>

        <h2 class="text-lg font-medium mt-4 text-gray-700 dark:text-gray-200" v-html="strikeSettings.form_sub_heading"/>

        <p class="mt-2 text-gray-700 dark:text-gray-200 text-sm">Jede teilnehmende Gruppe / Person erscheint mit einem Pin auf der Karte, so dass euer lokaler Aktionstag bundesweit auffindbar ist!</p>

        <p class="mt-2 text-gray-700 dark:text-gray-200 text-sm">Für jeden Pin öffnet sich ein Kontextmenü, in dem eure Webseite oder eure E-Mail Adresse verlinkt ist, zudem auch Zeitrahmen und Datum eurer Aktion. Interessierte erfahren so, wo die Adressen der teilnehmenden Heizungskeller in eurer Kommune veröffentlicht werden. Dafür tragt ihr in diesem Formular eure  Webseite oder eine (öffentliche) E-Mail-Adresse ein.</p>

        <p class="mt-2 text-gray-700 dark:text-gray-200 text-sm"><strong>Wichtig:</strong> Adressen der zu besichtigenden Häuser erscheinen nicht direkt auf der Karte!</p>

        <p class="mt-2 text-gray-700 dark:text-gray-200 text-sm">Ihr habt keine Webseite? Vielleicht aber ein Instagram-Account? Meldet Euch bei <a href="mailto:localzero@germanzero.de">localzero@germanzero.de</a>, wir finden eine Lösung!</p>
      </HeaderWithAction>
    </template>

    <form v-auto-animate class="-mx-4 sm:mx-0 inline-block space-y-4" @submit.prevent="submit">
      <Head :title="`${settings.strike_word_singular} eintragen`" />

      <TwoColumnFormSection
        :title="strikeSettings.form_general_title"
        :subtitle="strikeSettings.form_general_subtitle">
        <TESelect
          v-if="strikeSettings.city_required"
          v-model="form.city"
          name="city"
          :options="props.cities"
          :value-key="'id' as never"
          :display-key="'name' as never"
          label="Ortsgruppe"
          :error="form.errors.city"
        />

        <InputForData
          v-for="data in requiredData"
          :key="data.key"
          :data
          :form
        />

        <TEAlert type="info" title="Hinweis: Alles ist nachträglich bearbeitbar" class="mt-4">
          <template #icon><InformationCircleIcon class="size-8" /></template>
          Du kannst alle Angaben später noch ändern oder ergänzen, falls du dir jetzt noch nicht alles sicher bist oder sich später etwas ändert. Nach der ersten Überprüfung erhältst du einen Link an die von dir angegebene E-Mail-Adresse.
        </TEAlert>
      </TwoColumnFormSection>

      <TwoColumnFormSection
        v-if="showMore"
        :title="strikeSettings.form_advanced_title"
        :subtitle="strikeSettings.form_advanced_subtitle">
        <InputForData
          v-for="data in additionalData"
          :key="data.key"
          :data
          :form
        />

        <TESwitchLabel
          class="mt-4"
          :title="strikeSettings.privacy_title"
          :subtitle="strikeSettings.privacy_subtitle"
        >
          <TESwitch
            v-model="acceptedPrivacyPolicy"
          />
        </TESwitchLabel>

        <div class="mt-4 text-gray-500 dark:text-zinc-200 text-sm">Weitere Informationen zum Datenschutz findest du in unserer Datenschutzerklärung: <a class="underline" href="https://germanzero.de/datenschutz" target="_blank">Greenpeace</a>

         <a class="underline ml-2" href="https://localzero.net/datenschutz" target="_blank">LocalZero</a></div>

<!--        <div v-auto-animate> -->

<!--          <button v-if="!showMap" class="underline cursor-pointer text-sm dark:text-zinc-100" type="button" @click="showMapStuff">Genauen Startpunkt -->
<!--            auf -->
<!--            der Karte setzen -->
<!--          </button> -->

<!--          <TEAlert v-if="showMap" type="info" title="Hinweis zum Kartenpunkt"> -->
<!--            <template #icon><MapPinIcon class="size-8" /></template> -->
<!--            Verschiebe den Marker auf der Karte, oder klicke auf die Karte, um den genauen Startpunkt festzulegen. Sollte dies nicht klappen, verwende einen Computer mit Maus :) -->
<!--          </TEAlert> -->

<!--          <PointMap -->
<!--            v-if="showMap && form.location"  v-model:coordinates="form.location.coordinates" is-draggable :initial-zoom="5" -->
<!--            :height="400" /> -->
<!--        </div> -->

        <template #actions>
          <TEButton primary :disabled="!readyToSubmit || form.processing" @click="submit">Eintragen {{form.processing? '...' : ''}}</TEButton>
        </template>
      </TwoColumnFormSection>
    </form>
  </CenterLayout>
</template>
